/**
 * Reusable skeleton components for loading states
 */

// Base skeleton with pulse animation
export function Skeleton({ className = '', style = {} }) {
  return (
    <div
      className={`bg-surface-200 animate-pulse rounded ${className}`}
      style={style}
    />
  );
}

// Skeleton for transaction list item
export function TransactionSkeleton() {
  return (
    <div className="bg-white rounded-xl p-3 flex items-center gap-3 border border-surface-100">
      <Skeleton className="w-10 h-10 rounded-xl flex-shrink-0" />
      <div className="flex-1 min-w-0 space-y-2">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      <Skeleton className="h-5 w-20" />
    </div>
  );
}

// Skeleton for transaction list page
export function TransactionListSkeleton() {
  return (
    <div className="space-y-3 md:space-y-4">
      {/* Mobile Header */}
      <div className="xl:hidden mb-3">
        <Skeleton className="h-5 w-28 mb-1" />
        <Skeleton className="h-3 w-20" />
      </div>

      {/* Mobile Filters */}
      <div className="xl:hidden bg-white rounded-xl border border-surface-200 p-3 space-y-2">
        <div className="flex gap-2">
          <Skeleton className="flex-1 h-10 rounded-xl" />
          <Skeleton className="w-28 h-10 rounded-xl" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="flex-1 h-10 rounded-xl" />
          <Skeleton className="w-8 h-10" />
          <Skeleton className="flex-1 h-10 rounded-xl" />
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden xl:flex xl:items-center xl:justify-between gap-4">
        <div>
          <Skeleton className="h-7 w-36 mb-2" />
          <Skeleton className="h-4 w-24" />
        </div>
        <Skeleton className="h-12 w-40 rounded-xl" />
      </div>

      {/* Desktop Filters */}
      <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 p-4">
        <div className="flex gap-4">
          <Skeleton className="flex-1 h-10 rounded-xl" />
          <Skeleton className="w-32 h-10 rounded-xl" />
          <Skeleton className="w-40 h-10 rounded-xl" />
          <Skeleton className="w-40 h-10 rounded-xl" />
        </div>
      </div>

      {/* Mobile List */}
      <div className="xl:hidden space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <TransactionSkeleton key={i} />
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 overflow-hidden">
        <div className="p-4 border-b border-surface-200">
          <div className="flex gap-4">
            <Skeleton className="w-24 h-4" />
            <Skeleton className="flex-1 h-4" />
            <Skeleton className="w-24 h-4" />
            <Skeleton className="w-24 h-4" />
            <Skeleton className="w-24 h-4" />
            <Skeleton className="w-24 h-4" />
          </div>
        </div>
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="p-4 border-b border-surface-100 flex gap-4">
            <Skeleton className="w-24 h-4" />
            <Skeleton className="flex-1 h-4" />
            <Skeleton className="w-24 h-4" />
            <Skeleton className="w-24 h-4" />
            <Skeleton className="w-24 h-4" />
            <Skeleton className="w-16 h-4" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Skeleton for category page
export function CategoryPageSkeleton() {
  return (
    <div className="space-y-4">
      {/* Mobile Header */}
      <div className="xl:hidden mb-3">
        <Skeleton className="h-5 w-24 mb-1" />
        <Skeleton className="h-3 w-48" />
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl border border-surface-200 p-1.5">
        <div className="flex gap-1">
          <Skeleton className="flex-1 h-10 rounded-lg" />
          <Skeleton className="flex-1 h-10 rounded-lg" />
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden xl:flex xl:items-center xl:justify-between gap-4">
        <div>
          <Skeleton className="h-7 w-28 mb-2" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-12 w-36 rounded-xl" />
      </div>

      {/* Mobile Cards */}
      <div className="xl:hidden space-y-2">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="bg-white rounded-xl p-3 flex items-center gap-3 border border-surface-100">
            <Skeleton className="w-10 h-10 rounded-xl" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-3 w-16" />
            </div>
            <Skeleton className="w-16 h-6 rounded-full" />
          </div>
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 overflow-hidden">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="p-4 border-b border-surface-100 flex gap-4 items-center">
            <Skeleton className="w-10 h-10 rounded-xl" />
            <Skeleton className="flex-1 h-4" />
            <Skeleton className="w-20 h-4" />
            <Skeleton className="w-20 h-6 rounded-full" />
            <Skeleton className="w-16 h-4" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Skeleton for account page
export function AccountPageSkeleton() {
  return (
    <div className="space-y-3 md:space-y-4">
      {/* Mobile Header */}
      <div className="xl:hidden">
        <Skeleton className="h-6 w-28" />
      </div>

      {/* Mobile Hero Card */}
      <div className="xl:hidden rounded-2xl p-5 bg-surface-200 animate-pulse" style={{ height: 140 }} />

      {/* Mobile Cards */}
      <div className="xl:hidden space-y-3">
        {[1, 2].map((i) => (
          <div key={i} className="bg-white rounded-2xl p-4 shadow-sm">
            <div className="flex items-center gap-3 mb-3">
              <Skeleton className="w-11 h-11 rounded-xl" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-28" />
                <Skeleton className="h-3 w-20" />
              </div>
              <Skeleton className="h-5 w-24" />
            </div>
            <div className="flex gap-2 mb-3">
              <Skeleton className="flex-1 h-12 rounded-lg" />
              <Skeleton className="flex-1 h-12 rounded-lg" />
            </div>
            <div className="flex gap-1.5 pt-3 border-t border-surface-100">
              {[1, 2, 3, 4].map((j) => (
                <Skeleton key={j} className="flex-1 h-8 rounded-lg" />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Header */}
      <div className="hidden xl:flex xl:items-center xl:justify-between gap-4">
        <div>
          <Skeleton className="h-7 w-24 mb-2" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-12 w-36 rounded-xl" />
      </div>

      {/* Desktop Summary */}
      <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 p-4">
        <div className="flex gap-4">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-4 w-28" />
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 overflow-hidden">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-4 border-b border-surface-100 flex gap-4 items-center">
            <Skeleton className="w-10 h-10 rounded-xl" />
            <Skeleton className="flex-1 h-4" />
            <Skeleton className="w-24 h-4" />
            <Skeleton className="w-28 h-4" />
            <Skeleton className="w-16 h-4" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Skeleton for dashboard
export function DashboardSkeleton() {
  return (
    <div className="space-y-4">
      {/* Mobile Summary Cards */}
      <div className="xl:hidden grid grid-cols-2 gap-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-xl p-3 border border-surface-100">
            <Skeleton className="h-3 w-16 mb-2" />
            <Skeleton className="h-6 w-24" />
          </div>
        ))}
      </div>

      {/* Mobile Recent Transactions */}
      <div className="xl:hidden">
        <div className="flex justify-between items-center mb-3">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-4 w-16" />
        </div>
        <div className="space-y-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <TransactionSkeleton key={i} />
          ))}
        </div>
      </div>

      {/* Desktop Layout */}
      <div className="hidden xl:block">
        {/* Summary Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-white rounded-2xl p-5 border border-surface-200">
              <Skeleton className="h-4 w-20 mb-3" />
              <Skeleton className="h-8 w-32" />
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="bg-white rounded-2xl p-5 border border-surface-200">
            <Skeleton className="h-5 w-32 mb-4" />
            <Skeleton className="h-48 w-full rounded-xl" />
          </div>
          <div className="bg-white rounded-2xl p-5 border border-surface-200">
            <Skeleton className="h-5 w-32 mb-4" />
            <Skeleton className="h-48 w-full rounded-xl" />
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden">
          <div className="p-5 border-b border-surface-200 flex justify-between">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-4 w-16" />
          </div>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="p-4 border-b border-surface-100 flex gap-4 items-center">
              <Skeleton className="w-10 h-10 rounded-xl" />
              <Skeleton className="flex-1 h-4" />
              <Skeleton className="w-24 h-4" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Skeleton for recurring transactions page
export function RecurringTransactionsSkeleton() {
  return (
    <div className="space-y-3 md:space-y-4">
      {/* Mobile Header */}
      <div className="xl:hidden mb-3">
        <Skeleton className="h-5 w-44 mb-1" />
        <Skeleton className="h-3 w-56" />
      </div>

      {/* Mobile Summary */}
      <div className="xl:hidden bg-white rounded-xl border border-surface-200 p-3">
        <div className="grid grid-cols-3 gap-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-surface-50 rounded-lg p-2.5">
              <Skeleton className="h-2.5 w-10 mb-1" />
              <Skeleton className="h-4 w-8" />
            </div>
          ))}
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden xl:flex xl:items-center xl:justify-between gap-4">
        <div>
          <Skeleton className="h-7 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <Skeleton className="h-12 w-40 rounded-xl" />
      </div>

      {/* Desktop Summary */}
      <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 p-4">
        <div className="flex gap-4">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-4 w-20" />
        </div>
      </div>

      {/* Mobile Cards */}
      <div className="xl:hidden space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-xl p-3 flex items-center gap-3 border border-surface-100">
            <Skeleton className="w-10 h-10 rounded-xl" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-24" />
            </div>
            <div className="text-right space-y-1">
              <Skeleton className="h-4 w-16 ml-auto" />
              <Skeleton className="h-5 w-12 ml-auto rounded-full" />
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 overflow-hidden">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-4 border-b border-surface-100 flex gap-4 items-center">
            <Skeleton className="w-10 h-10 rounded-xl" />
            <Skeleton className="flex-1 h-4" />
            <Skeleton className="w-20 h-6 rounded-full" />
            <Skeleton className="w-24 h-4" />
            <Skeleton className="w-24 h-4" />
            <Skeleton className="w-16 h-4" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Skeleton for ledger page
export function LedgerPageSkeleton() {
  return (
    <div className="space-y-4 pb-24 md:pb-6">
      {/* Mobile Header */}
      <div className="xl:hidden flex items-center justify-between mb-3">
        <div>
          <Skeleton className="h-5 w-16 mb-1" />
          <Skeleton className="h-3 w-40" />
        </div>
        <Skeleton className="w-10 h-10 rounded-xl" />
      </div>

      {/* Desktop Header */}
      <div className="hidden xl:flex items-center justify-between">
        <div>
          <Skeleton className="h-7 w-20 mb-2" />
          <Skeleton className="h-4 w-52" />
        </div>
        <div className="flex gap-2">
          <Skeleton className="h-10 w-24 rounded-xl" />
          <Skeleton className="h-10 w-20 rounded-xl" />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-xl md:rounded-2xl border border-surface-100 md:border-surface-200 p-3 md:p-4">
            <div className="flex items-center gap-2 mb-1 md:mb-2">
              <Skeleton className="w-7 h-7 md:w-8 md:h-8 rounded-lg" />
              <Skeleton className="h-3 md:h-4 w-14" />
            </div>
            <Skeleton className="h-5 md:h-7 w-24" />
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl md:rounded-2xl border border-surface-100 md:border-surface-200 p-3 md:p-4">
        {/* Mobile Filters */}
        <div className="xl:hidden space-y-3">
          <Skeleton className="h-10 w-full rounded-xl" />
          <div className="flex gap-2">
            <Skeleton className="flex-1 h-10 rounded-xl" />
            <Skeleton className="flex-1 h-10 rounded-xl" />
          </div>
          <div className="flex gap-2 items-center">
            <Skeleton className="flex-1 h-10 rounded-xl" />
            <Skeleton className="h-3 w-4" />
            <Skeleton className="flex-1 h-10 rounded-xl" />
          </div>
        </div>

        {/* Desktop Filters */}
        <div className="hidden xl:flex flex-wrap gap-3 items-center">
          <Skeleton className="flex-1 min-w-[200px] max-w-xs h-10 rounded-xl" />
          <Skeleton className="w-32 h-10 rounded-xl" />
          <Skeleton className="w-32 h-10 rounded-xl" />
          <Skeleton className="w-32 h-10 rounded-xl" />
          <Skeleton className="w-28 h-10 rounded-xl" />
          <Skeleton className="w-24 h-10 rounded-xl" />
        </div>
      </div>

      {/* Mobile Ledger Cards */}
      <div className="xl:hidden space-y-2">
        {/* Opening Balance */}
        <div className="bg-surface-50 rounded-xl p-3 border border-surface-200">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-4 w-28" />
            </div>
            <Skeleton className="h-5 w-20" />
          </div>
        </div>
        {/* Transaction Cards */}
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="bg-white rounded-xl p-3 border border-surface-100">
            <div className="flex items-start gap-3">
              <Skeleton className="w-10 h-10 rounded-xl flex-shrink-0" />
              <div className="flex-1 min-w-0 space-y-1">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-3 w-1/2" />
              </div>
            </div>
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-surface-50">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-4 w-24" />
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Ledger Table */}
      <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-surface-50 border-b border-surface-200">
              <th className="px-4 py-3 text-left w-28"><Skeleton className="h-3 w-10" /></th>
              <th className="px-4 py-3 text-left"><Skeleton className="h-3 w-20" /></th>
              <th className="px-4 py-3 text-left w-32"><Skeleton className="h-3 w-16" /></th>
              <th className="px-4 py-3 text-left w-28"><Skeleton className="h-3 w-14" /></th>
              <th className="px-4 py-3 text-right w-28"><Skeleton className="h-3 w-10 ml-auto" /></th>
              <th className="px-4 py-3 text-right w-28"><Skeleton className="h-3 w-10 ml-auto" /></th>
              <th className="px-4 py-3 text-right w-32"><Skeleton className="h-3 w-14 ml-auto" /></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-100">
            {/* Opening Balance Row */}
            <tr className="bg-surface-50">
              <td className="px-4 py-3"><Skeleton className="h-4 w-20" /></td>
              <td className="px-4 py-3" colSpan={3}><Skeleton className="h-4 w-28" /></td>
              <td className="px-4 py-3"><Skeleton className="h-4 w-6" /></td>
              <td className="px-4 py-3"><Skeleton className="h-4 w-6" /></td>
              <td className="px-4 py-3 text-right"><Skeleton className="h-4 w-24 ml-auto" /></td>
            </tr>
            {/* Transaction Rows */}
            {[1, 2, 3, 4, 5].map((i) => (
              <tr key={i}>
                <td className="px-4 py-3">
                  <Skeleton className="h-4 w-20 mb-1" />
                  <Skeleton className="h-3 w-12" />
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <Skeleton className="w-7 h-7 rounded-lg flex-shrink-0" />
                    <Skeleton className="h-4 w-40" />
                  </div>
                </td>
                <td className="px-4 py-3"><Skeleton className="h-4 w-20" /></td>
                <td className="px-4 py-3"><Skeleton className="h-4 w-20" /></td>
                <td className="px-4 py-3 text-right"><Skeleton className="h-4 w-16 ml-auto" /></td>
                <td className="px-4 py-3 text-right"><Skeleton className="h-4 w-16 ml-auto" /></td>
                <td className="px-4 py-3 text-right"><Skeleton className="h-4 w-20 ml-auto" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Skeleton for stats page
export function StatsPageSkeleton() {
  return (
    <div className="space-y-4 pb-4">
      {/* Mobile Header */}
      <div className="xl:hidden mb-3">
        <Skeleton className="h-5 w-12 mb-1" />
        <Skeleton className="h-3 w-32" />
      </div>

      {/* Mobile Filters */}
      <div className="xl:hidden bg-white rounded-xl border border-surface-200 p-3 space-y-3">
        {/* Type Tabs */}
        <div className="flex gap-2">
          <Skeleton className="flex-1 h-10 rounded-xl" />
          <Skeleton className="flex-1 h-10 rounded-xl" />
          <Skeleton className="flex-1 h-10 rounded-xl" />
        </div>
        {/* Period and Bank Filters */}
        <div className="grid grid-cols-2 gap-2">
          <Skeleton className="h-10 rounded-xl" />
          <Skeleton className="h-10 rounded-xl" />
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden xl:block">
        <Skeleton className="h-7 w-16 mb-2" />
        <Skeleton className="h-4 w-36" />
      </div>

      {/* Desktop Filters */}
      <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 p-4 space-y-4">
        {/* Type Tabs */}
        <div className="flex gap-2">
          <Skeleton className="flex-1 h-10 rounded-xl" />
          <Skeleton className="flex-1 h-10 rounded-xl" />
          <Skeleton className="flex-1 h-10 rounded-xl" />
        </div>
        {/* Period and Bank Filters */}
        <div className="grid grid-cols-2 gap-3">
          <Skeleton className="h-10 rounded-xl" />
          <Skeleton className="h-10 rounded-xl" />
        </div>
      </div>

      {/* Chart Card */}
      <div className="bg-white rounded-xl md:rounded-2xl border border-surface-100 md:border-surface-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <Skeleton className="h-5 w-36" />
          <Skeleton className="h-4 w-28" />
        </div>
        {/* Donut Chart Placeholder */}
        <div className="h-64 flex items-center justify-center">
          <Skeleton className="w-48 h-48 rounded-full" />
        </div>
        {/* Legend */}
        <div className="flex flex-wrap gap-2 justify-center mt-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-1.5 px-2 py-1">
              <Skeleton className="w-3 h-3 rounded-full" />
              <Skeleton className="h-3 w-16" />
            </div>
          ))}
        </div>
      </div>

      {/* Category List */}
      <div className="bg-white rounded-xl md:rounded-2xl border border-surface-100 md:border-surface-200 overflow-hidden">
        <div className="p-4 border-b border-surface-100">
          <Skeleton className="h-5 w-24" />
        </div>
        <div className="divide-y divide-surface-100">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="flex items-center gap-3 p-4">
              <Skeleton className="w-3 h-3 rounded-full flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-1">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-20" />
                </div>
                <div className="flex items-center justify-between mb-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-3 w-10" />
                </div>
                <Skeleton className="h-1.5 w-full rounded-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// Skeleton for receivables page
export function ReceivablesSkeleton() {
  return (
    <div className="space-y-3 md:space-y-4">
      {/* Mobile Header */}
      <div className="xl:hidden mb-3">
        <Skeleton className="h-5 w-28 mb-1" />
        <Skeleton className="h-3 w-40" />
      </div>

      {/* Mobile Summary */}
      <div className="xl:hidden bg-white rounded-xl border border-surface-200 p-3">
        <div className="grid grid-cols-2 gap-2">
          {[1, 2].map((i) => (
            <div key={i} className="bg-surface-50 rounded-lg p-2.5">
              <Skeleton className="h-2.5 w-16 mb-1" />
              <Skeleton className="h-4 w-20" />
            </div>
          ))}
        </div>
        <div className="mt-2 flex justify-center gap-3">
          <Skeleton className="h-2.5 w-16" />
          <Skeleton className="h-2.5 w-16" />
          <Skeleton className="h-2.5 w-16" />
        </div>
      </div>

      {/* Desktop Header */}
      <div className="hidden xl:flex xl:items-center xl:justify-between gap-4">
        <div>
          <Skeleton className="h-7 w-32 mb-2" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-12 w-40 rounded-xl" />
      </div>

      {/* Desktop Summary */}
      <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 p-4">
        <div className="flex gap-4">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-40" />
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-8 w-20 rounded-lg" />
        ))}
      </div>

      {/* Mobile Cards */}
      <div className="xl:hidden space-y-2">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white rounded-xl p-3 border border-surface-100">
            <div className="flex items-start gap-3">
              <Skeleton className="w-10 h-10 rounded-xl" />
              <div className="flex-1 space-y-1">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-2.5 w-28" />
              </div>
              <Skeleton className="h-5 w-16 rounded-full" />
            </div>
            <div className="flex justify-between mt-2 pt-2 border-t border-surface-100">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-4 w-28" />
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 overflow-hidden">
        {[1, 2, 3].map((i) => (
          <div key={i} className="p-4 border-b border-surface-100 flex gap-4 items-center">
            <Skeleton className="w-10 h-10 rounded-xl" />
            <Skeleton className="flex-1 h-4" />
            <Skeleton className="w-24 h-4" />
            <Skeleton className="w-24 h-4" />
            <Skeleton className="w-20 h-4" />
            <Skeleton className="w-20 h-4" />
            <Skeleton className="w-16 h-6 rounded-full" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Skeleton for debts page (same as receivables)
export function DebtsSkeleton() {
  return <ReceivablesSkeleton />;
}

// Skeleton for contacts page
export function ContactsSkeleton() {
  return (
    <div className="space-y-3 md:space-y-4">
      {/* Mobile Header */}
      <div className="xl:hidden mb-3">
        <Skeleton className="h-5 w-24 mb-1" />
        <Skeleton className="h-3 w-48" />
      </div>

      {/* Desktop Header */}
      <div className="hidden xl:flex xl:items-center xl:justify-between gap-4">
        <div>
          <Skeleton className="h-7 w-24 mb-2" />
          <Skeleton className="h-4 w-52" />
        </div>
        <Skeleton className="h-12 w-36 rounded-xl" />
      </div>

      {/* Search */}
      <Skeleton className="h-10 w-full rounded-xl" />

      {/* Mobile Cards */}
      <div className="xl:hidden space-y-2">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-white rounded-xl p-3 border border-surface-100 flex items-center gap-3">
            <Skeleton className="w-10 h-10 rounded-xl" />
            <div className="flex-1 space-y-1">
              <Skeleton className="h-4 w-28" />
              <div className="flex gap-3">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
            <div className="flex gap-0.5">
              <Skeleton className="w-7 h-7 rounded-lg" />
              <Skeleton className="w-7 h-7 rounded-lg" />
              <Skeleton className="w-7 h-7 rounded-lg" />
            </div>
          </div>
        ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden xl:block bg-white rounded-2xl border border-surface-200 overflow-hidden">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="p-4 border-b border-surface-100 flex gap-4 items-center">
            <Skeleton className="w-10 h-10 rounded-xl" />
            <Skeleton className="flex-1 h-4" />
            <Skeleton className="w-48 h-4" />
            <Skeleton className="w-32 h-4" />
            <div className="flex gap-1">
              <Skeleton className="w-8 h-8 rounded-lg" />
              <Skeleton className="w-8 h-8 rounded-lg" />
              <Skeleton className="w-8 h-8 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
